import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchTicketTags, addTicket } from '@/store/Ticket';
import { Button } from '@mui/material';
import { toast } from 'react-toastify';

type Tags = {
  id: string;
  name: string;
};

interface Props {
  onClose?: () => void;
}

const AddSupportTicket: React.FC<Props> = ({ onClose }) => {
  const dispatch = useDispatch<any>();

  const [selectedTag, setSelectedTag] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [ticketPic, setTicketPic] = useState<File | null>(null);
  const [agree, setAgree] = useState<boolean>(false);
  const [tags, setTags] = useState<Tags[]>([]);

  // Fetch tags on mount
  useEffect(() => {
    const fetchTags = async () => {
      const res = await dispatch(fetchTicketTags());
      setTags(res.payload.data);
    };
    fetchTags();
  }, []);

  const handleSubmit = () => {
    if (!selectedTag || !description.trim()) {
      toast.info('Issue Tag and Description are required.');
      return;
    }
    if (!agree) {
      toast.info('You must agree to the terms.');
      return;
    }

    const formData = new FormData();
    formData.append('issueTagName', selectedTag);
    formData.append('description', description);
    if (ticketPic) {
      formData.append('ticketPic', ticketPic);
    }

    dispatch(addTicket(formData))
      .unwrap()
      .then(() => {
        toast.success('Ticket submitted successfully');
        // Reset form
        setSelectedTag('');
        setDescription('');
        setTicketPic(null);
        setAgree(false);
        onClose?.();
      })
      .catch((err: any) => {
        toast.error('Failed to submit ticket: ' + err.message);
      });
  };

  return (
    <div className='h-full flex items-center justify-center'>
      <div className='w-full max-w-md bg-white rounded-[24px] p-6 shadow-lg flex flex-col space-y-6'>
        {/* Issue Tag */}
        <div className='space-y-2'>
          <div className='flex justify-between items-center font-medium'>
            <span>Issue Tag</span>
            <span className='text-xl cursor-pointer'>▾</span>
          </div>
          <div className='bg-gray-100 rounded-lg p-3 flex flex-col space-y-3 max-h-40 overflow-auto'>
            {tags?.map((tag: any) => (
              <label
                key={tag.id}
                className='flex items-center bg-white rounded-lg p-3 space-x-3 cursor-pointer'
              >
                <input
                  type='radio'
                  name='issue'
                  value={tag.name}
                  checked={selectedTag === tag.name}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  className='accent-[#FF0050]'
                />
                <span>{tag.name}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Description */}
        <div className='flex flex-col space-y-1'>
          <label className='text-sm text-gray-600'>Description</label>
          <textarea
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className='w-full bg-gray-100 rounded-lg p-3 resize-none focus:outline-none'
          />
        </div>

        {/* Screenshot */}
        <div className='flex flex-col space-y-1'>
          <label className='text-sm text-gray-600'>Screenshot</label>
          <div className='flex items-center space-x-3'>
            <input
              type='file'
              accept='image/png,image/jpeg'
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setTicketPic(e.target.files[0]);
                }
              }}
              className='hidden'
              id='ticketPicUpload'
            />
            <label
              htmlFor='ticketPicUpload'
              className='px-4 py-2 border border-black rounded-full text-sm cursor-pointer'
            >
              Add Image
            </label>
            {ticketPic && <span className='text-sm'>{ticketPic.name}</span>}
          </div>
        </div>

        {/* Terms */}
        <label className='flex items-center space-x-2 text-sm'>
          <input
            type='checkbox'
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
            className='accent-[#FF0050]'
          />
          <span>
            Agree with{' '}
            <span className='text-[#14D8CE] underline'>terms & Conditions</span>
          </span>
        </label>

        {/* Actions */}
        <div className='flex justify-end space-x-3'>
          <Button
            variant='outlined'
            disabled={!selectedTag || !description.trim() || !agree || !ticketPic}
            onClick={handleSubmit}
            sx={{
              textTransform: 'none',
              borderRadius: 100,
              px: 4,
              color: 'black',
              borderColor: 'black',
            }}
          >
            Submit Ticket
          </Button>
          <Button
            variant='contained'
            onClick={() => {
              setSelectedTag('');
              setDescription('');
              setTicketPic(null);
              setAgree(false);
              onClose?.();
            }}
            sx={{
              textTransform: 'none',
              borderRadius: 100,
              px: 4,
              bgcolor: '#FF0050',
              '&:hover': { bgcolor: '#E6004F' },
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddSupportTicket;
